import { useCallback, useEffect, useState } from 'react';
import { Keyboard, View } from 'react-native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view';
import { BodyWrapper } from '@/layouts/BodyWrapper';
import { Text } from '@/components/Text';
import { Card } from '@/components/ui/therapist/Card';
import { Input } from '@/components/ui/therapist/Input';
import { Icon } from '@/components/Icon';
import theme from '@/config/theme';
import {
  createTherapistTags,
  deleteTherapistTags,
  fetchTherapistTags,
  updateTherapistTag,
} from '@/api/services/therapistTags';
import { TherapistTag } from '@/stores/store.types';
import { logError } from '@/utils/logHandling';
import { styles } from './styles';
import { Button } from '@/components/ui/therapist/Button';
import { useDialog } from '@/hooks/useDialog';
import { useNotification } from '@/components/ui/therapist/Notification/NotificationProvider';
import { useResponsiveHeight } from '@/hooks/useResponsiveDimensions';

const patientCountLabel = (count: number) =>
  count === 1 ? '1 Patient:in' : `${count} Patient:innen`;

const byId = (a: TherapistTag, b: TherapistTag) => b._id.localeCompare(a._id);

export const TherapistTags: React.FC = () => {
  const [therapistTags, setTherapistTags] = useState<TherapistTag[]>([]);
  const [editingTagId, setEditingTagId] = useState<string | null>(null);
  const [nameBeforeEdit, setNameBeforeEdit] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const { showDialog } = useDialog();
  const { showNotification } = useNotification();

  const extraHeight = useResponsiveHeight(30);

  const loadTherapistTags = useCallback(async () => {
    try {
      setTherapistTags((await fetchTherapistTags()).sort(byId));
    } catch (error) {
      logError('Could not fetch therapist tags', error);
    }
  }, []);

  useEffect(() => {
    loadTherapistTags();
  }, [loadTherapistTags]);

  const handleTagNameChange = (tagId: string, name: string) =>
    setTherapistTags((prev) =>
      prev.map((tag) => (tag._id === tagId ? { ...tag, name } : tag)),
    );

  const startEditing = (tag: TherapistTag) => {
    setNameBeforeEdit(tag.name);
    setEditingTagId(tag._id);
  };

  const cancelEditing = (tagId: string) => {
    handleTagNameChange(tagId, nameBeforeEdit);
    setEditingTagId(null);
  };

  const saveTagName = async (tag: TherapistTag) => {
    setIsSaving(true);
    try {
      await updateTherapistTag(tag._id, tag.name.trim());
      handleTagNameChange(tag._id, tag.name.trim());
      setEditingTagId(null);
    } catch (error) {
      logError('Could not update therapist tag', error);
      handleTagNameChange(tag._id, nameBeforeEdit);
      showNotification({
        type: 'error',
        title: 'Der Filterbegriff konnte nicht gespeichert werden.',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSavePress = (tag: TherapistTag) => {
    Keyboard.dismiss();
    const assignedCount = tag.patients?.length ?? 0;

    if (!assignedCount) {
      saveTagName(tag);
      return;
    }

    showDialog({
      title: 'Filterbegriff wird bereits verwendet',
      content: `Dieser Filterbegriff ist ${patientCountLabel(assignedCount)} zugewiesen. Der neue Name wird bei allen zugewiesenen Patient:innen übernommen.`,
      showCancelButton: true,
      cancelAction: {
        label: 'Abbrechen',
        onPress: (_e, onClose) => onClose?.(),
      },
      confirmAction: {
        label: 'Speichern',
        onPress: (_e, onClose) => {
          saveTagName(tag);
          onClose?.();
        },
      },
    });
  };

  const deleteTag = async (tag: TherapistTag) => {
    setIsDeleting(true);
    try {
      await deleteTherapistTags([tag._id]);
      setTherapistTags((prev) => prev.filter(({ _id }) => _id !== tag._id));
    } catch (error) {
      logError('Could not delete therapist tag', error);
      showNotification({
        type: 'error',
        title: 'Der Filterbegriff konnte nicht gelöscht werden.',
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeletePress = (tag: TherapistTag) => {
    const assignedCount = tag.patients?.length ?? 0;

    showDialog({
      title: `„${tag.name}“ löschen?`,
      content: assignedCount
        ? `Dieser Filterbegriff ist mit ${patientCountLabel(assignedCount)} verknüpft. Er wird nach dem Löschen aus allen Profilen entfernt und kann nicht wiederhergestellt werden.`
        : 'Dieser Filterbegriff kann nach dem Löschen nicht wiederhergestellt werden.',
      danger: true,
      showCancelButton: true,
      cancelAction: {
        label: 'Abbrechen',
        onPress: (_e, onClose) => onClose?.(),
      },
      confirmAction: {
        label: 'Löschen',
        onPress: (_e, onClose) => {
          deleteTag(tag);
          onClose?.();
        },
      },
    });
  };

  const trimmedNewTagName = newTagName.trim();
  const isDuplicateNewTag = therapistTags.some(
    (tag) => tag.name.trim().toLowerCase() === trimmedNewTagName.toLowerCase(),
  );

  const createTag = async () => {
    Keyboard.dismiss();
    setIsCreating(true);
    try {
      await createTherapistTags([trimmedNewTagName]);
      await loadTherapistTags();
      setNewTagName('');
    } catch (error) {
      logError('Could not create therapist tag', error);
      showNotification({
        type: 'error',
        title: 'Der Filterbegriff konnte nicht angelegt werden.',
      });
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <BodyWrapper>
      <KeyboardAwareScrollView
        enableOnAndroid
        keyboardShouldPersistTaps="handled"
        extraHeight={extraHeight}
        extraScrollHeight={30}
        contentContainerStyle={{ paddingBottom: 200 }}
      >
        <Card title="Patient:innen Filterbegriffe verwalten" icon="hash">
          <Text style={{ color: theme.colors.newUI.therapist.grey70 }}>
            Fügen Sie z. B. behandelnde Therapeut:innen oder Stationen hinzu, um
            Patient:innen in der Übersicht schneller filtern zu können.
          </Text>
          <View style={styles.tagRow}>
            <View style={styles.tagInfo}>
              <Icon
                name="hash"
                size={20}
                color={theme.colors.newUI.therapist.primaryBlue80}
              />
              <Input
                value={newTagName}
                onChange={setNewTagName}
                placeholder="Neuen Filterbegriff eingeben"
                disabled={!!editingTagId}
                status={isDuplicateNewTag ? 'warning' : 'default'}
                statusMessage="Dieser Filterbegriff existiert bereits."
                style={styles.tagInput}
                testID="info-tag-new-input"
              />
            </View>
            <Button
              icon="plus"
              label="Hinzufügen"
              disabled={
                !!editingTagId ||
                isCreating ||
                !trimmedNewTagName ||
                isDuplicateNewTag
              }
              onPress={createTag}
              testID="info-tag-add"
            />
          </View>
          {therapistTags.map((tag) => {
            const isEditing = editingTagId === tag._id;
            const trimmedName = tag.name.trim();

            return (
              <View key={tag._id} style={styles.tagRow}>
                <View style={styles.tagInfo}>
                  <Icon
                    name="hash"
                    size={20}
                    color={theme.colors.newUI.therapist.primaryBlue80}
                  />
                  <Input
                    autoFocus={isEditing}
                    value={tag.name}
                    onChange={(name) => handleTagNameChange(tag._id, name)}
                    disabled={!isEditing}
                    style={styles.tagInput}
                    testID={`info-tag-input-${tag._id}`}
                  />
                  <Text style={styles.patientCount}>
                    {patientCountLabel(tag.patients?.length ?? 0)}
                  </Text>
                </View>
                <View style={styles.tagActions}>
                  {isEditing ? (
                    <>
                      <Button
                        icon="check"
                        look="secondary"
                        disabled={
                          isSaving ||
                          !trimmedName ||
                          trimmedName === nameBeforeEdit
                        }
                        onPress={() => handleSavePress(tag)}
                        testID={`info-tag-save-${tag._id}`}
                      />
                      <Button
                        icon="x"
                        look="secondary"
                        disabled={isSaving}
                        onPress={() => cancelEditing(tag._id)}
                        testID={`info-tag-cancel-${tag._id}`}
                      />
                    </>
                  ) : (
                    <>
                      <Button
                        icon="edit"
                        look="secondary"
                        disabled={!!editingTagId || isDeleting}
                        onPress={() => startEditing(tag)}
                        testID={`info-tag-edit-${tag._id}`}
                      />
                      <Button
                        icon="delete"
                        look="secondary"
                        disabled={!!editingTagId || isDeleting}
                        onPress={() => handleDeletePress(tag)}
                        testID={`info-tag-delete-${tag._id}`}
                      />
                    </>
                  )}
                </View>
              </View>
            );
          })}
        </Card>
      </KeyboardAwareScrollView>
    </BodyWrapper>
  );
};
